import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type {
  PathLookupEdgeSettings,
  TopologyEdgeSettings,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import type { DropdownMenuProps } from 'components/DropdownMenu/DropdownMenu';
import { OverflowMenu } from 'components/DropdownMenu/OverflowMenu';
import { OverflowMenuButtonLegacy } from 'components/DropdownMenu/OverflowMenuButton';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { setGroupedEdge } from 'components/Graph/commands/setGroupedEdge';
import { setVisibleEdge } from 'components/Graph/commands/setVisibleEdge';
import { useMemo } from 'react';
import type { FC } from 'react';

type Props = {
  disabled?: boolean;
  childProtocols: (TopologyEdgeSettings | PathLookupEdgeSettings)[];
};

export const ProtocolGroupActions: FC<Props> = (props) => {
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
  });

  const visibleProtocols = useMemo(
    () => props.childProtocols.filter(prop('visible')),
    [props.childProtocols],
  );
  const someVisible = visibleProtocols.length > 0;
  const someHidden = visibleProtocols.length < props.childProtocols.length;
  const makeSetAllVisible = useStableCallback((visible: boolean) => () => {
    ctxProps.dispatch(setVisibleEdge, {
      edgeSettingsId: props.childProtocols.map(prop('id')),
      visible,
    });
  });

  const someGrouped = useMemo(
    () => visibleProtocols.some(prop('grouped')),
    [visibleProtocols],
  );
  const someUngrouped = useMemo(
    () => visibleProtocols.some((p) => !p.grouped),
    [visibleProtocols],
  );
  const makeSetAllGrouped = useStableCallback((grouped: boolean) => () => {
    ctxProps.dispatch(setGroupedEdge, {
      edgeSettingsId: visibleProtocols.map(prop('id')),
      grouped,
    });
  });

  const overflowMenuItems: DropdownMenuProps['items'] = useMemo(
    () => [
      {
        label: 'Show all protocols',
        icon: 'fa-eye',
        disabled: !someHidden,
        onClick: makeSetAllVisible(true),
      },
      {
        label: 'Hide all protocols',
        icon: 'fa-eye-slash',
        disabled: !someVisible,
        onClick: makeSetAllVisible(false),
      },
      {
        label: 'Group all protocols',
        icon: 'fa-link',
        disabled: !someUngrouped,
        onClick: makeSetAllGrouped(true),
      },
      {
        label: 'Ungroup all protocols',
        icon: 'fa-unlink',
        disabled: !someGrouped,
        onClick: makeSetAllGrouped(false),
      },
    ],
    [someGrouped, someUngrouped, someVisible, someHidden],
  );
  return (
    <OverflowMenu
      disabled={props.disabled}
      ButtonComponent={OverflowMenuButtonLegacy}
      items={overflowMenuItems}
    />
  );
};
